import React, { useContext } from 'react';

import { CardCheckbox } from '@components/NextForms/CardCheckbox';
import { CheckboxBooleanInput } from '@components/NextForms/CheckboxBooleanInput';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { ManagementFormLabel } from '@components/NextForms/ManagementForm';
import { TextInput } from '@components/NextForms/TextInput';
import { CustomsInstructionContext } from '@components/utilities/Bookings/helpers2';
import { InstructionInputContainer } from '@components/utilities/Bookings/instructionComponents';
import { instructionStrings } from '@data/instruction';
import { getDecodeResult, unwrapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { observer } from 'mobx-react';

const useStyles = makeMuiStyles((theme, common) => ({
  specialNoteOption: {
    ...common.flexColumn,
    justifyContent: 'center',
    paddingLeft: 40,
    width: '100%',
    height: '100%',
  },
  specialNoteDisplay: {
    ...common.backgroundPrimaryAlpha3,
    padding: '1rem',
    width: '100%',
  },
  specialNoteDisplayTitle: {
    ...common.textBlack,
    ...common.normalText,
    ...common.fontWeight600,
  },
}));

export const CustomsInstructionSpecialNote: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { isEditing, form } = useContext(CustomsInstructionContext);

  const { fields } = form.root;

  if (isEditing) {
    return (
      <>
        <ManagementFormLabel field={fields.has_special_note} required />

        <InstructionInputContainer>
          <CardCheckbox
            field={fields.has_special_note}
            label={
              <span className={classes.specialNoteOption}>
                {instructionStrings.customsInstructionHasSpecialNoteFalse}
              </span>
            }
            value={false}
            size="small"
          />
          <CardCheckbox
            field={fields.has_special_note}
            label={
              <span className={classes.specialNoteOption}>
                {instructionStrings.customsInstructionHasSpecialNoteTrue}
              </span>
            }
            value={true}
            size="small"
          />
        </InstructionInputContainer>

        {getDecodeResult(fields.has_special_note.decodeResult) && (
          <>
            <ManagementFormLabel field={fields.special_note} required />

            <InstructionInputContainer paddingTopForLabel>
              {fields.special_note.elements.map((element) => {
                const value = getDecodeResult(element.decodeResult);

                if (!value) return null;
                return (
                  <div key={value.title}>
                    <label>
                      <CheckboxBooleanInput field={element.fields.selected} />
                      <span style={{ marginLeft: 8, cursor: 'pointer' }}>{value.title}</span>
                    </label>

                    {value.selected && (
                      <div style={{ paddingLeft: 22, paddingTop: 8, paddingBottom: 8 }}>
                        <TextInput
                          field={element.fields.note}
                          multiline
                          inputProps={{
                            placeholder:
                              instructionStrings.customsInstructionSpecialNotePlaceholder(
                                value.title,
                              ),
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              <ErrorMessages field={fields.special_note} />
            </InstructionInputContainer>
          </>
        )}
      </>
    );
  }

  const [has_special_note, special_note] = [
    unwrapDecodeResult(fields.has_special_note.decodeResult),
    unwrapDecodeResult(fields.special_note.decodeResult),
  ];

  return (
    <>
      <ManagementFormLabel field={fields.has_special_note} />
      <InstructionInputContainer>
        <div className={classes.specialNoteDisplay}>
          {has_special_note === false && (
            <div className={classes.specialNoteDisplayTitle}>
              {instructionStrings.customsInstructionHasSpecialNoteFalse}
            </div>
          )}

          {has_special_note === true &&
            special_note
              .filter((f) => f.selected === true)
              .map((field) => (
                <div key={field.title}>
                  <div className={classes.specialNoteDisplayTitle}>{field.title}</div>

                  {field.note.trim().length > 0 && (
                    <div style={{ paddingTop: '5px', paddingBottom: '5px' }}>{field.note}</div>
                  )}

                  {field.note.trim().length <= 0 && (
                    <div style={{ paddingTop: '5px', paddingBottom: '5px' }}>-</div>
                  )}
                </div>
              ))}
        </div>
      </InstructionInputContainer>
    </>
  );
});
